import { useState } from 'react';
import { getImageUrl } from '../../../config';
import { trackToolClick } from '../../../lib/trackToolClick';
import initials from '../../../lib/initials';

function StarIcon({ filled }) {
  return (
    <svg className="app-card-star-svg" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function OpenIcon() {
  return (
    <svg className="app-link__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/**
 * Same pattern as profile/ui/Avatar: a missing or broken icon_url (a stale
 * upload path) falls back to the tool's initials, never a broken-image glyph
 * or an empty, unexplained gap.
 */
export function ToolIcon({ tool }) {
  const [broken, setBroken] = useState(false);
  if (tool.icon_url && !broken) {
    return <img src={getImageUrl(tool.icon_url)} alt="" onError={() => setBroken(true)} />;
  }
  return <span className="app-icon__fallback" aria-hidden="true">{initials(tool.name)}</span>;
}

/**
 * One tool tile, shared by the home page and the classic My tools page.
 *
 * The card is a div holding two siblings: the link (stretched over the whole
 * card by `.app-card__link::after`) and the favourite button. The button used
 * to sit INSIDE the <a>, which is invalid HTML (interactive content in a link)
 * and handed keyboard and screen-reader users a control nested in a control.
 */
export default function ToolTile({ tool, isFavorite, onToggleFavorite }) {
  return (
    <div className={`app-card ${isFavorite ? 'app-card--favorite' : ''}`} data-category={tool.category}>
      <a
        href={tool.link}
        className="app-card__link"
        // The title lives on the link, not the spans: the link's stretched
        // ::after covers the whole card, so every hover lands on the <a> and a
        // span's title would never show.
        title={tool.description ? `${tool.name}: ${tool.description}` : tool.name}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackToolClick(tool.id)}
      >
        <span className="app-icon"><ToolIcon tool={tool} /></span>
        <span className="app-info">
          <span className="app-name">{tool.name}</span>
          {tool.description && (
            <span className="app-description">{tool.description}</span>
          )}
        </span>
      </a>
      <div className="app-card-right">
        <button
          type="button"
          className={`app-card-star ${isFavorite ? 'app-card-star--on' : ''}`}
          aria-label={isFavorite ? `Remove ${tool.name} from favorites` : `Add ${tool.name} to favorites`}
          aria-pressed={isFavorite}
          onClick={() => onToggleFavorite(tool.id)}
        >
          <StarIcon filled={isFavorite} />
        </button>
        <span className="app-link" aria-hidden="true">Open <OpenIcon /></span>
      </div>
    </div>
  );
}
